import React, { useState } from 'react';

/**
 * Componente de vista previa en tiempo real del perfil público
 * Renderiza el perfil según la plantilla y colores seleccionados
 * IDÉNTICO al perfil público real (backend/static_page_generator.py, version movil)
 */

// ---- Helpers de color, portados 1:1 desde backend/static_page_generator.py ----

const hexToRgb = (hex) => {
  let h = (hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const num = parseInt(h, 16);
  if (h.length !== 6 || isNaN(num)) return [128, 128, 128];
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
};

const relativeLuminance = (r, g, b) => {
  const channel = (c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const isDarkColor = (hex) => {
  const [r, g, b] = hexToRgb(hex);
  return relativeLuminance(r, g, b) < 0.35;
};

const ensureReadableText = (txtColor, bgColor) => {
  const [bgR, bgG, bgB] = hexToRgb(bgColor);
  const [txR, txG, txB] = hexToRgb(txtColor);
  const bgLum = relativeLuminance(bgR, bgG, bgB);
  const txLum = relativeLuminance(txR, txG, txB);
  const lighter = Math.max(bgLum, txLum);
  const darker = Math.min(bgLum, txLum);
  const contrast = (lighter + 0.05) / (darker + 0.05);
  if (contrast >= 4.5) return txtColor;
  return bgLum > 0.5 ? '#111827' : '#f9fafb';
};

const darkenHex = (hex, factor) => {
  const [r, g, b] = hexToRgb(hex);
  const clamp = (v) => Math.max(0, Math.min(255, Math.trunc(v)));
  const toHex = (v) => clamp(v).toString(16).padStart(2, '0');
  return `#${toHex(r * factor)}${toHex(g * factor)}${toHex(b * factor)}`;
};

const normalizeUrl = (url) => {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

// Free-plan watermark — mirrors static_page_generator.py's WATERMARK_LOGO_URL
// 1:1 so the dashboard preview never disagrees with the real public page.
const WATERMARK_LOGO_URL = 'https://res.cloudinary.com/dfyzvch4i/image/upload/e_trim/v1787372846/WhatsApp_Image_2026-08-16_at_10.57.14_PM_7_-Photoroom_oxzabs.png';

const WatermarkBadge = () => (
  <div style={{ position: 'absolute', top: 14, left: 14, zIndex: 20, lineHeight: 0 }}>
    <img
      src={WATERMARK_LOGO_URL}
      alt="Contact"
      style={{ height: 18, width: 'auto', display: 'block', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.35))' }}
    />
  </div>
);

// Mirrors TRANSFER_ACCOUNT_TYPE_LABELS in static_page_generator.py
const TRANSFER_ACCOUNT_TYPE_LABELS = {
  corriente: 'Cuenta Corriente',
  vista: 'Cuenta Vista',
  ahorro: 'Cuenta de Ahorro',
  rut: 'Cuenta RUT',
};

const BankIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10l9-6 9 6M4 10v9m16-9v9M9 19v-6h6v6M4 19h16" />
  </svg>
);

const CopyIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="9" y="9" width="12" height="12" rx="2.2" />
    <path strokeLinecap="round" d="M5 15H4a1 1 0 01-1-1V4a1 1 0 011-1h10a1 1 0 011 1v1" />
  </svg>
);

const CheckIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

// Verified badge shown next to the name on all templates — mirrors ICONS['verified'] in static_page_generator.py
const VerifiedBadge = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#2563EB" style={{ flexShrink: 0 }}>
    <path d="M12 2l2.4 1.9 3-.6 1.1 2.8 2.8 1.1-.6 3 1.9 2.4-1.9 2.4.6 3-2.8 1.1-1.1 2.8-3-.6L12 22l-2.4-1.9-3 .6-1.1-2.8-2.8-1.1.6-3L1.4 12l1.9-2.4-.6-3 2.8-1.1L6.6 2.9l3 .6L12 2z" />
    <path d="M9 12.5l2 2 4-4.5" stroke="#fff" strokeWidth="1.7" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ProfilePreview = ({ profile, template = 'classic', colors, isPreview = false, showWatermark = false }) => {
  const API = '/api';
  const [transferCopied, setTransferCopied] = useState(false);

  // Datos de transferencia: mismo orden y misma construcción que
  // _build_transfer_rows en el backend. Copiar sí funciona en preview
  // (a diferencia de abrir un link) — no navega fuera del dashboard.
  const getTransferRows = () => {
    if (!profile?.transfer_enabled) return [];
    const rows = [];
    if (profile.transfer_bank) rows.push({ label: 'Banco', value: profile.transfer_bank });
    if (profile.transfer_account_type) {
      rows.push({ label: 'Tipo de cuenta', value: TRANSFER_ACCOUNT_TYPE_LABELS[profile.transfer_account_type] || profile.transfer_account_type });
    }
    if (profile.transfer_account_number) rows.push({ label: 'Número de cuenta', value: profile.transfer_account_number });
    if (profile.transfer_holder_name) rows.push({ label: 'Nombre del titular', value: profile.transfer_holder_name });
    if (profile.transfer_rut) rows.push({ label: 'RUT', value: profile.transfer_rut });
    if (profile.transfer_email) rows.push({ label: 'Email para comprobante', value: profile.transfer_email });
    return rows;
  };

  // Copia todos los campos juntos, de una — el mismo modelo que usa la
  // propia app del banco al "compartir datos de transferencia" (un solo
  // bloque de texto, no campo por campo).
  const handleCopyAllTransfer = (rows) => {
    const text = rows.map((r) => `${r.label}: ${r.value}`).join('\n');
    const markCopied = () => {
      setTransferCopied(true);
      setTimeout(() => setTransferCopied(false), 1400);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(markCopied).catch(markCopied);
    } else {
      markCopied();
    }
  };

  // Función para obtener la imagen del perfil
  const getProfileImage = () => {
    if (profile?.profile_image_url) return profile.profile_image_url;
    if (profile?.profile_image_base64) {
      return `data:${profile.profile_image_type || 'image/jpeg'};base64,${profile.profile_image_base64}`;
    }
    if (profile?.profile_image) return `${API}/files/${profile.profile_image}`;
    return null;
  };

  // Función para obtener la imagen de fondo
  const getBackgroundImage = () => {
    if (profile?.background_image_url) return profile.background_image_url;
    if (profile?.background_image_base64) {
      return `data:${profile.background_image_type || 'image/jpeg'};base64,${profile.background_image_base64}`;
    }
    if (profile?.background_image) return `${API}/files/${profile.background_image}`;
    return null;
  };

  // Enlaces configurados: mismo orden y misma construcción que _build_links en el backend
  const getConfiguredLinks = () => {
    const links = [];
    const fullName = `${profile?.name || ''} ${profile?.lastname || ''}`.trim();

    if (profile?.email) {
      links.push({ type: 'email', value: `mailto:${profile.email}`, label: 'Email', icon: 'email' });
    }
    if (profile?.whatsapp) {
      const cleanNumber = profile.whatsapp.replace(/[^0-9]/g, '');
      const msg = `Hola ${fullName}, te conocí a través de tu perfil Contact y quería ponerme en contacto contigo.`;
      links.push({ type: 'whatsapp', value: `https://wa.me/${cleanNumber}?text=${encodeURIComponent(msg)}`, label: 'WhatsApp', icon: 'whatsapp' });
    }
    if (profile?.linkedin) {
      links.push({ type: 'linkedin', value: normalizeUrl(profile.linkedin), label: 'LinkedIn', icon: 'linkedin' });
    }
    if (profile?.instagram) {
      let instagramUrl;
      if (profile.instagram.startsWith('http://') || profile.instagram.startsWith('https://')) {
        instagramUrl = profile.instagram;
      } else {
        const username = profile.instagram.replace('@', '').trim();
        instagramUrl = `https://instagram.com/${username}`;
      }
      links.push({ type: 'instagram', value: instagramUrl, label: 'Instagram', icon: 'instagram' });
    }
    if (profile?.facebook) {
      links.push({ type: 'facebook', value: normalizeUrl(profile.facebook), label: 'Facebook', icon: 'facebook' });
    }
    if (profile?.website) {
      links.push({ type: 'website', value: normalizeUrl(profile.website), label: 'Sitio Web', icon: 'website' });
    }
    for (let i = 1; i <= 4; i++) {
      const link = profile?.[`custom_link_${i}`];
      const title = profile?.[`custom_link_title_${i}`];
      if (link && title) {
        links.push({ type: 'custom', value: normalizeUrl(link), label: title, icon: 'link' });
      }
    }
    if (profile?.tiktok) {
      const tk = profile.tiktok;
      const tkUrl = tk.startsWith('http') ? tk : `https://tiktok.com/@${tk.replace('@', '').trim()}`;
      links.push({ type: 'tiktok', value: tkUrl, label: 'TikTok', icon: 'tiktok' });
    }
    ['', '_2', '_3'].forEach((suffix) => {
      const fileUrl = profile?.[`custom_file_url${suffix}`];
      const fileName = profile?.[`custom_file_name${suffix}`];
      if (fileUrl && fileName) {
        links.push({ type: 'file', value: fileUrl, label: fileName, icon: 'file' });
      }
    });

    return links;
  };

  // Función para manejar clicks en enlaces (funcionalidad real)
  const handleLinkClick = (link) => {
    if (isPreview) {
      return;
    }
    window.open(link.value, '_blank');
  };

  // Función para descargar VCF
  const handleSaveContact = () => {
    if (isPreview) {
      return;
    }

    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${profile.name} ${profile.lastname || ''}
N:${profile.lastname || ''};${profile.name};;;
${profile.position ? `TITLE:${profile.position}\n` : ''}${profile.company ? `ORG:${profile.company}\n` : ''}${profile.email ? `EMAIL:${profile.email}\n` : ''}${profile.phone ? `TEL:${profile.phone}\n` : ''}${profile.website ? `URL:${profile.website}\n` : ''}END:VCARD`;

    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${profile.name}_${profile.lastname || ''}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Iconos SVG para cada tipo de enlace - LOGOS ORIGINALES DE MARCA
  const getIcon = (iconType) => {
    const icons = {
      linkedin: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      facebook: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      instagram: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      ),
      whatsapp: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
      email: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      website: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      ),
      link: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      tiktok: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
        </svg>
      ),
      file: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      users: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-5.513-3.806-10-9.5-10S2 12.487 2 18v2m10-20c2.175 0 4.5 2.821 4.5 5s-2.325 5-4.5 5-4.5-2.821-4.5-5 2.325-5 4.5-5" />
        </svg>
      ),
      bell: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      )
    };

    return icons[iconType] || icons.link;
  };

  const configuredLinks = getConfiguredLinks();
  const transferRows = getTransferRows();
  const profileImage = getProfileImage();
  const backgroundImage = getBackgroundImage();

  // Resolución de colores: mismo orden que generate_static_page en el backend
  const bg = colors?.background || '#ffffff';
  const btn = colors?.button || '#3b82f6';
  const isDark = isDarkColor(bg);
  const txt = ensureReadableText(colors?.text || '#000000', bg);
  const btnTxt = ensureReadableText('#ffffff', btn);

  // Plantilla Clásica - estilo inmersivo fan carousel
  if (template === 'classic') {
    const btnDark = darkenHex(btn, 0.58);
    const bgStyle = backgroundImage
      ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : { background: `linear-gradient(160deg, ${btn} 0%, ${btnDark} 100%)` };
    const overlayGradient = backgroundImage
      ? 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.38) 45%, rgba(0,0,0,0.76) 100%)'
      : 'linear-gradient(to bottom, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.20) 45%, rgba(0,0,0,0.52) 100%)';

    return (
      <div
        className="relative w-full overflow-hidden flex flex-col items-center justify-end"
        style={{ ...bgStyle, minHeight: isPreview ? 580 : '100vh', paddingTop: 52, paddingBottom: 36, paddingLeft: 20, paddingRight: 20 }}
      >
        {/* Dark gradient overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: overlayGradient }}
        />

        {showWatermark && <WatermarkBadge />}

        {/* Content */}
        <div className="relative z-10 w-full text-center" style={{ maxWidth: 480 }}>

          {/* Avatar */}
          <div className="relative mx-auto mb-4" style={{ width: 88, height: 88 }}>
            {profileImage ? (
              <img
                src={profileImage}
                alt={profile?.name}
                className="rounded-full object-cover w-full h-full"
                style={{ border: '3px solid rgba(255,255,255,0.85)', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
              />
            ) : (
              <div
                className="rounded-full w-full h-full flex items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.15)', border: '3px solid rgba(255,255,255,0.4)' }}
              >
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
            {/* NFC dot */}
            <div
              className="absolute bottom-0 right-0 rounded-full flex items-center justify-center"
              style={{ width: 22, height: 22, background: btn, border: '2.5px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.35)' }}
            >
              <div className="rounded-full bg-white" style={{ width: 8, height: 8 }} />
            </div>
          </div>

          {/* Name */}
          <h1 className="font-bold text-white mb-1 flex items-center justify-center gap-1.5" style={{ fontSize: 26, letterSpacing: '-0.4px', textShadow: '0 1px 6px rgba(0,0,0,0.4)', lineHeight: 1.2 }}>
            {profile?.name} {profile?.lastname}
            <VerifiedBadge size={20} />
          </h1>

          {/* Position */}
          {profile?.position && (
            <p className="text-sm font-medium mb-1" style={{ color: 'rgba(255,255,255,0.85)' }}>
              {profile.position}
            </p>
          )}

          {/* Company */}
          {profile?.company && (
            <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
              {profile.company}
            </p>
          )}

          {/* Bio */}
          {profile?.bio && (
            <p className="text-sm mb-5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
              {profile.bio}
            </p>
          )}

          {/* Save Contact button */}
          <button
            className="w-full font-bold flex items-center justify-center gap-2 transition-all mb-3"
            style={{ padding: '15px 24px', borderRadius: 999, border: 'none', background: '#fff', color: btn, fontSize: 15, fontWeight: 700, boxShadow: '0 4px 20px rgba(0,0,0,0.2)', cursor: isPreview ? 'default' : 'pointer' }}
            onClick={handleSaveContact}
            disabled={isPreview}
          >
            <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Guardar contacto
          </button>

          {/* Social Links */}
          <div className="w-full space-y-2.5 mb-3.5">
            {configuredLinks.map((link, index) => (
              <button
                key={index}
                onClick={() => handleLinkClick(link)}
                disabled={isPreview}
                className="w-full text-sm font-medium flex items-center justify-center gap-3 transition-all"
                style={{
                  padding: '13px 20px',
                  borderRadius: 14,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1.5px solid rgba(255,255,255,0.28)',
                  color: '#fff',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  cursor: isPreview ? 'default' : 'pointer'
                }}
              >
                <span style={{ color: btn }}>{getIcon(link.icon)}</span>
                <span>{link.label}</span>
              </button>
            ))}
          </div>

          {/* Datos de transferencia */}
          {transferRows.length > 0 && (
            <div className="w-full text-left mb-3.5" style={{ background: 'rgba(255,255,255,0.10)', border: '1.5px solid rgba(255,255,255,0.22)', borderRadius: 14, padding: '12px 16px 16px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
              <div className="flex items-center gap-1.5" style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.04em', paddingBottom: 10 }}>
                <BankIcon /><span>Datos para transferencia</span>
              </div>
              <button
                onClick={() => handleCopyAllTransfer(transferRows)}
                className="w-full flex items-center justify-center transition-all"
                style={{ gap: 8, padding: '11px 16px', borderRadius: 12, border: '1.5px solid rgba(255,255,255,0.28)', background: transferCopied ? '#34d399' : 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {transferCopied ? <CheckIcon /> : <CopyIcon />}
                <span>{transferCopied ? 'Copiado' : 'Copiar datos de transferencia'}</span>
              </button>
            </div>
          )}

          {/* Identificarme */}
          <div className="w-full flex justify-center mb-4">
            <button
              disabled={isPreview}
              className="text-sm font-medium flex items-center justify-center gap-2 transition-all"
              style={{
                padding: '12px 28px',
                borderRadius: 14,
                border: '1.5px solid rgba(255,255,255,0.28)',
                background: 'rgba(255,255,255,0.10)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                cursor: isPreview ? 'default' : 'pointer'
              }}
            >
              {getIcon('bell')}
              <span>Identificarme</span>
            </button>
          </div>

          <p className="text-white font-semibold" style={{ fontSize: 10, opacity: 0.3, letterSpacing: '0.15em' }}>
            POWERED BY CONTACT
          </p>
        </div>
      </div>
    );
  }

  // Plantilla Corporate - banner superior + info al lado, version mobile (igual a @media(max-width:600px) del perfil real)
  if (template === 'corporate') {
    const cardBorder = isDark ? '#334155' : 'rgba(229,231,235,0.5)';
    const linkBg = isDark ? 'rgba(30,41,59,0.6)' : 'rgba(248,250,252,0.8)';
    const subtleText = isDark ? '#cbd5e1' : '#4b5563';
    const companyColor = isDark ? '#c4b5fd' : btn;
    const bannerStyle = backgroundImage
      ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : { background: `linear-gradient(135deg, ${btn}, ${btn}cc)` };

    return (
      <div className="w-full relative" style={{ backgroundColor: bg, color: txt, minHeight: isPreview ? 580 : '100vh' }}>
        {showWatermark && <WatermarkBadge />}

        {/* Banner */}
        <div className="relative w-full" style={{ ...bannerStyle, height: 200 }}>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.4))' }} />
        </div>

        <div className="px-6" style={{ marginTop: -50 }}>
          {/* Header: foto + nombre, centrado (version mobile) */}
          <div className="flex flex-col items-center text-center relative z-10" style={{ paddingBottom: 24, borderBottom: `1px solid ${cardBorder}` }}>
            {profileImage ? (
              <img
                src={profileImage}
                alt={profile?.name}
                className="object-cover"
                style={{ width: 100, height: 100, borderRadius: 16, border: `4px solid ${bg}`, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
              />
            ) : (
              <div
                className="flex items-center justify-center"
                style={{ width: 100, height: 100, borderRadius: 16, background: isDark ? '#1e293b' : '#e2e8f0', border: `4px solid ${bg}` }}
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 48, height: 48, color: '#94a3b8' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
            <h1 className="font-bold flex items-center justify-center gap-1.5" style={{ fontSize: 24, color: txt, letterSpacing: '-0.5px', lineHeight: 1.2, marginTop: 12 }}>
              {profile?.name} {profile?.lastname}
              <VerifiedBadge size={21} />
            </h1>
            {profile?.position && (
              <div style={{ fontSize: 15, color: txt, opacity: 0.8, marginTop: 2 }}>{profile.position}</div>
            )}
            {profile?.company && (
              <div style={{ fontSize: 14, fontWeight: 600, color: companyColor, marginTop: 4 }}>{profile.company}</div>
            )}
          </div>

          {profile?.bio && (
            <p style={{ fontSize: 15, lineHeight: 1.7, color: txt, opacity: 0.9, padding: '24px 0' }}>{profile.bio}</p>
          )}

          {/* Guardar contacto */}
          <div className="flex" style={{ paddingBottom: 32, paddingTop: profile?.bio ? 0 : 24 }}>
            <button
              onClick={handleSaveContact}
              disabled={isPreview}
              className="flex items-center justify-center gap-2.5 transition-all"
              style={{ flex: 1, padding: '16px 24px', borderRadius: 12, border: 'none', background: btn, color: btnTxt, fontSize: 15, fontWeight: 600, boxShadow: `0 4px 12px ${btn}30`, cursor: isPreview ? 'default' : 'pointer' }}
            >
              <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Guardar contacto
            </button>
          </div>

          {/* Contacto y redes */}
          {configuredLinks.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.5px', color: subtleText, marginBottom: 16 }}>
                Contacto y redes
              </div>
              <div className="grid grid-cols-1" style={{ gap: 12, paddingBottom: 32 }}>
                {configuredLinks.map((link, index) => (
                  <button
                    key={index}
                    onClick={() => handleLinkClick(link)}
                    disabled={isPreview}
                    className="flex items-center gap-3 transition-all"
                    style={{
                      padding: '14px 18px',
                      borderRadius: 12,
                      background: linkBg,
                      border: `1.5px solid ${btn}25`,
                      color: txt,
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: isPreview ? 'default' : 'pointer'
                    }}
                  >
                    <span style={{ width: 16, height: 16, flexShrink: 0 }}>{getIcon(link.icon)}</span>
                    <span>{link.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Datos de transferencia */}
          {transferRows.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.5px', color: subtleText, marginBottom: 16, paddingTop: 24 }}>
                Datos para transferencia
              </div>
              <div style={{ background: linkBg, border: `1.5px solid ${cardBorder}`, borderRadius: 14, padding: 18, marginBottom: 32 }}>
                <button
                  onClick={() => handleCopyAllTransfer(transferRows)}
                  className="w-full flex items-center justify-center transition-all"
                  style={{ gap: 8, padding: '12px 16px', borderRadius: 12, border: `1.5px solid ${btn}30`, background: transferCopied ? '#10b981' : `${btn}12`, color: transferCopied ? '#fff' : btn, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
                >
                  {transferCopied ? <CheckIcon /> : <CopyIcon />}
                  <span>{transferCopied ? 'Copiado' : 'Copiar datos de transferencia'}</span>
                </button>
              </div>
            </>
          )}

          {/* Identificarme */}
          <div className="flex justify-center" style={{ paddingBottom: 32 }}>
            <button
              disabled={isPreview}
              className="flex items-center gap-2 transition-all"
              style={{ padding: '14px 32px', borderRadius: 12, border: `1.5px solid ${btn}30`, background: 'transparent', color: txt, fontSize: 15, fontWeight: 500, cursor: isPreview ? 'default' : 'pointer' }}
            >
              {getIcon('bell')}
              <span>Identificarme</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Plantilla Retrato - foto a página completa que se funde en un panel de
  // color debajo. Espejo 1:1 de _render_portrait en static_page_generator.py.
  if (template === 'portrait') {
    const [tR, tG, tB] = hexToRgb(txt);
    const tc1 = txt;
    const tc2 = `rgba(${tR},${tG},${tB},0.68)`;
    const tc3 = `rgba(${tR},${tG},${tB},0.5)`;
    const divider = `rgba(${tR},${tG},${tB},0.14)`;
    const wmColor = `rgba(${tR},${tG},${tB},0.4)`;
    const btnDark = darkenHex(btn, 0.58);

    const BRAND_COLORS = {
      linkedin: '#0A66C2',
      whatsapp: '#25D366',
      facebook: '#1877F2',
      tiktok: '#000000',
      instagram: 'radial-gradient(circle at 30% 110%, #FDF497 0%, #FDF497 5%, #FD5949 45%, #D6249F 60%, #285AEB 90%)',
    };
    const LINK_SUBTITLES = {
      email: 'Escríbeme directo',
      whatsapp: 'Escríbeme por WhatsApp',
      linkedin: 'Conectemos en LinkedIn',
      instagram: 'Sígueme en Instagram',
      facebook: 'Sígueme en Facebook',
      website: 'Visita mi sitio web',
      tiktok: 'Sígueme en TikTok',
      file: 'Descarga el archivo',
    };

    return (
      <div className="w-full relative flex flex-col" style={{ backgroundColor: bg, minHeight: isPreview ? 580 : '100vh' }}>
        {/* Hero: foto a ancho completo (object-fit:cover, nunca franjas laterales).
            flex:1 hace que el hero ceda espacio al panel cuando hay muchos
            links, y crezca cuando el perfil es simple — así los perfiles
            típicos caben en una pantalla sin scroll. */}
        <div className="relative w-full overflow-hidden" style={{ flex: '1 1 auto', minHeight: 200, maxHeight: 320 }}>
          {profileImage ? (
            <>
              <img
                src={profileImage}
                alt={profile?.name}
                className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
                style={{ objectPosition: 'center 12%' }}
              />
              <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{ top: '60%', background: `linear-gradient(to bottom, transparent 0%, ${bg} 100%)` }} />
            </>
          ) : (
            <>
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(160deg, ${btn} 0%, ${btnDark} 100%)` }}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 48, height: 48, color: 'rgba(255,255,255,0.6)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{ top: '60%', background: `linear-gradient(to bottom, transparent 0%, ${bg} 100%)` }} />
            </>
          )}
        </div>

        {/* Panel */}
        <div className="relative flex-shrink-0" style={{ padding: '16px 18px 24px', background: bg }}>
          <div className="font-bold flex items-center gap-1.5" style={{ fontSize: 19, color: tc1, letterSpacing: '-0.01em' }}>
            {profile?.name} {profile?.lastname}
            <VerifiedBadge size={15} />
          </div>
          {profile?.position && (
            <div style={{ fontSize: 13, color: tc2, marginTop: 3, fontWeight: 500, lineHeight: 1.35 }}>{profile.position}</div>
          )}
          {profile?.company && (
            <div style={{ fontSize: 13, color: tc3, lineHeight: 1.35 }}>{profile.company}</div>
          )}
          {profile?.bio && (
            <p style={{ fontSize: 12.5, color: tc2, lineHeight: 1.55, marginTop: 11 }}>{profile.bio}</p>
          )}

          <button
            onClick={handleSaveContact}
            disabled={isPreview}
            className="w-full flex items-center justify-center gap-2 transition-all"
            style={{ marginTop: 16, marginBottom: 13, padding: 13, borderRadius: 999, border: 'none', background: '#fff', color: '#0A0A0A', fontSize: 14, fontWeight: 700, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 6px 18px rgba(0,0,0,0.08)', cursor: isPreview ? 'default' : 'pointer' }}
          >
            <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Guardar contacto
          </button>

          <div className="flex flex-col">
            {configuredLinks.map((link, index) => {
              const iconBg = BRAND_COLORS[link.type] || btn;
              const iconColor = BRAND_COLORS[link.type] ? '#fff' : btnTxt;
              const subtitle = LINK_SUBTITLES[link.type];
              return (
                <button
                  key={index}
                  onClick={() => handleLinkClick(link)}
                  disabled={isPreview}
                  className="flex items-center text-left transition-all"
                  style={{ gap: 11, padding: '10px 0', borderBottom: index < configuredLinks.length - 1 ? `1px solid ${divider}` : 'none', background: 'transparent', border: 'none', cursor: isPreview ? 'default' : 'pointer' }}
                >
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 10, background: iconBg, color: iconColor }}>
                    <span style={{ width: 15, height: 15, display: 'flex' }}>{getIcon(link.icon)}</span>
                  </div>
                  <div className="min-w-0">
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: tc1, lineHeight: 1.3 }}>{link.label}</div>
                    {subtitle && <div style={{ fontSize: 11, color: tc3, marginTop: 1, lineHeight: 1.3 }}>{subtitle}</div>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Datos de transferencia */}
          {transferRows.length > 0 && (
            <>
              <div className="flex items-center" style={{ gap: 7, fontSize: 11, fontWeight: 600, color: tc3, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 14, marginBottom: 4 }}>
                <BankIcon size={13} /><span>Datos para transferencia</span>
              </div>
              <button
                onClick={() => handleCopyAllTransfer(transferRows)}
                className="w-full flex items-center justify-center transition-all"
                style={{ gap: 7, padding: '10px 14px', borderRadius: 10, border: `1px solid ${btn}30`, background: transferCopied ? '#10b981' : `${btn}15`, color: transferCopied ? '#fff' : btn, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
              >
                {transferCopied ? <CheckIcon size={13} /> : <CopyIcon size={13} />}
                <span>{transferCopied ? 'Copiado' : 'Copiar datos de transferencia'}</span>
              </button>
            </>
          )}

          {/* Identificarme */}
          <div className="w-full flex justify-center" style={{ marginTop: 16 }}>
            <button
              disabled={isPreview}
              className="text-sm font-medium flex items-center justify-center gap-2 transition-all"
              style={{ padding: '10px 24px', borderRadius: 999, border: `1px solid ${btn}30`, background: `${btn}18`, color: tc1, fontSize: 13.5, fontWeight: 600, cursor: isPreview ? 'default' : 'pointer' }}
            >
              {getIcon('bell')}
              <span>Identificarme</span>
            </button>
          </div>

          <p className="text-center" style={{ fontSize: 10, color: wmColor, marginTop: 14 }}>
            Hecho con Contact
          </p>
        </div>

        {showWatermark && (
          <div style={{ position: 'absolute', top: 14, left: 14, zIndex: 20, lineHeight: 0, background: 'rgba(0,0,0,0.4)', padding: '6px 9px', borderRadius: 999, display: 'flex', alignItems: 'center' }}>
            <img src={WATERMARK_LOGO_URL} alt="Contact" style={{ height: 15, width: 'auto', display: 'block' }} />
          </div>
        )}
      </div>
    );
  }

  // Fallback
  return <div>Plantilla no encontrada</div>;
};

export default ProfilePreview;
